import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import { track } from "../lib/posthog";
import { reportar } from "../utils/reportar";

// ── Mappers MSI: snake_case (backend) ↔ camelCase (frontend) ──
const mapMsiFromBackend = (c) => ({
  id: c.id,
  concepto: c.concepto || "",
  cat: c.categoria || "Otros",
  totalCompra: Number(c.total_compra) || 0,
  meses: Number(c.num_meses) || 12,
  mesInicio: (Number(c.mes_inicio) || 1) - 1, // backend 1-12 → frontend 0-11
  anioInicio: Number(c.anio_inicio) || new Date().getFullYear(),
  cuentaId: c.cuenta_id || null,
  pagos: {},
});

export function useGlobalData() {
  const [metas, setMetas] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [recurrentes, setRecurrentes] = useState([]);
  const [msiCompras, setMsiCompras] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [transferencias, setTransferencias] = useState([]);
  const [diaCobro, setDiaCobro] = useState(15); // default día 15
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Auto-sync refs ──
  const backendSnap = useRef({ metas: [], presupuestos: [], recurrentes: [] });
  const pendingIds = useRef({ metas: new Set(), presupuestos: new Set(), recurrentes: new Set() });
  const dbTimers = useRef({});
  const didInit = useRef(false);

  // Fetch all global data
  const fetchGlobalData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [metasRes, presupuestosRes, recurrentesRes, msiComprasRes, cuentasRes, categoriasRes, transferenciasRes, preferenciasRes] =
        await Promise.all([
          api.metas.list(),
          api.presupuestos.list(),
          api.recurrentes.list(),
          api.msi.compras.list(),
          api.cuentas.list(),
          api.categorias.list(),
          api.transferencias.list(),
          api.preferencias.getAll().catch(() => ({})),
        ]);

      setMetas(metasRes || []);
      setPresupuestos((presupuestosRes || []).map(p => ({
        id: p.id, cat: p.categoria || "", limite: Number(p.limite_mensual) || 0,
      })));
      setRecurrentes((recurrentesRes || []).map(r => ({
        id: r.id, concepto: r.concepto || "", monto: Number(r.monto) || 0,
        tipo: r.tipo || "fijo", cat: r.categoria || "Otros",
        activo: r.activo !== false, dia_mes: r.dia_mes || null, cuenta_id: r.cuenta_id || null,
      })));
      setCuentas(cuentasRes || []);
      setTransferencias(transferenciasRes || []);
      setCategorias(categoriasRes || []);
      if (preferenciasRes?.dia_cobro) setDiaCobro(Number(preferenciasRes.dia_cobro));
      if (preferenciasRes?.nombre_usuario) setNombreUsuario(preferenciasRes.nombre_usuario);
      if (preferenciasRes?.onboarding_completado === "true") setOnboardingCompleted(true);

      // Initialize backend snapshot for auto-sync
      backendSnap.current = {
        metas: [...(metasRes || [])],
        presupuestos: [...(presupuestosRes || [])],
        recurrentes: [...(recurrentesRes || [])],
      };
      didInit.current = true;

      // Mark loading done — UI can render while MSI pagos load in background
      setLoading(false);

      // Mapear compras MSI de snake_case a camelCase + cargar pagos (non-blocking)
      const rawCompras = msiComprasRes || [];
      const mappedCompras = rawCompras.map(mapMsiFromBackend);
      setMsiCompras(mappedCompras);

      // Cargar pagos para cada compra en background
      if (mappedCompras.length > 0) {
        const pagosPromises = rawCompras.map(c => api.msi.pagos.list(c.id));
        Promise.all(pagosPromises).then(allPagos => {
          const withPagos = mappedCompras.map((compra, i) => {
            const pagosArr = allPagos[i] || [];
            const pagosMap = {};
            pagosArr.forEach(p => {
              const mesIdx = (Number(p.mes) || 1) - 1;
              pagosMap[mesIdx] = { pagado: p.pagado, montoPagado: p.pagado ? p.monto_cuota : null, pagoId: p.id };
            });
            return { ...compra, pagos: pagosMap };
          });
          setMsiCompras(withPagos);
        }).catch(err => console.error("MSI pagos fetch failed:", err));
      }
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch global data:", err);
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchGlobalData();
  }, [fetchGlobalData]);

  // ── Auto-sync: metas ──
  useEffect(() => {
    if (!didInit.current) return;
    const snap = backendSnap.current.metas;
    const pend = pendingIds.current.metas;
    // Deletes
    for (const b of [...snap]) {
      if (!metas.find(l => l.id === b.id)) {
        api.metas.remove(b.id).catch(reportar("borrar meta"));
        backendSnap.current.metas = backendSnap.current.metas.filter(x => x.id !== b.id);
      }
    }
    // Creates
    for (const l of metas) {
      if (!snap.find(b => b.id === l.id) && !pend.has(l.id)) {
        pend.add(l.id);
        api.metas.create({ id: l.id, nombre: l.nombre || "", icono: l.icono || "⭐", objetivo: l.objetivo || 0 })
          .then(res => {
            pend.delete(l.id);
            const row = Array.isArray(res) ? res[0] : res;
            backendSnap.current.metas = [...backendSnap.current.metas, row || { id: l.id }];
          })
          .catch((e) => { pend.delete(l.id); reportar("crear meta")(e); });
      }
    }
    // Updates (debounced 1.5s)
    const changed = metas.filter(l => {
      const b = snap.find(b => b.id === l.id);
      return b && (l.nombre !== b.nombre || l.icono !== b.icono || Number(l.objetivo) !== Number(b.objetivo));
    });
    if (changed.length > 0) {
      clearTimeout(dbTimers.current.metas);
      dbTimers.current.metas = setTimeout(() => {
        changed.forEach(m => {
          const f = { nombre: m.nombre, icono: m.icono, objetivo: m.objetivo };
          api.metas.update(m.id, f).then(() => {
            backendSnap.current.metas = backendSnap.current.metas.map(b => b.id === m.id ? { ...b, ...f } : b);
          }).catch(reportar("actualizar meta"));
        });
      }, 1500);
    }
  }, [metas]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-sync: presupuestos ──
  useEffect(() => {
    if (!didInit.current) return;
    const snap = backendSnap.current.presupuestos;
    const pend = pendingIds.current.presupuestos;
    for (const b of [...snap]) {
      if (!presupuestos.find(l => l.id === b.id)) {
        api.presupuestos.remove(b.id).catch(reportar("borrar presupuesto"));
        backendSnap.current.presupuestos = backendSnap.current.presupuestos.filter(x => x.id !== b.id);
      }
    }
    for (const l of presupuestos) {
      if (!snap.find(b => b.id === l.id) && !pend.has(l.id)) {
        pend.add(l.id);
        api.presupuestos.create({ id: l.id, categoria: l.cat || "", limite_mensual: l.limite || 0 })
          .then(res => {
            pend.delete(l.id);
            const row = Array.isArray(res) ? res[0] : res;
            backendSnap.current.presupuestos = [...backendSnap.current.presupuestos, row || { id: l.id }];
          })
          .catch((e) => { pend.delete(l.id); reportar("crear presupuesto")(e); });
      }
    }
    const changed = presupuestos.filter(l => {
      const b = snap.find(b => b.id === l.id);
      return b && (l.cat !== b.categoria || Number(l.limite) !== Number(b.limite_mensual));
    });
    if (changed.length > 0) {
      clearTimeout(dbTimers.current.presupuestos);
      dbTimers.current.presupuestos = setTimeout(() => {
        changed.forEach(p => {
          const f = { categoria: p.cat, limite_mensual: p.limite };
          api.presupuestos.update(p.id, f).then(() => {
            backendSnap.current.presupuestos = backendSnap.current.presupuestos.map(b => b.id === p.id ? { ...b, ...f } : b);
          }).catch(reportar("actualizar entidad global"));
        });
      }, 1500);
    }
  }, [presupuestos]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-sync: recurrentes ──
  useEffect(() => {
    if (!didInit.current) return;
    const snap = backendSnap.current.recurrentes;
    const pend = pendingIds.current.recurrentes;
    for (const b of [...snap]) {
      if (!recurrentes.find(l => l.id === b.id)) {
        api.recurrentes.remove(b.id).catch(reportar("borrar recurrente"));
        backendSnap.current.recurrentes = backendSnap.current.recurrentes.filter(x => x.id !== b.id);
      }
    }
    for (const l of recurrentes) {
      if (!snap.find(b => b.id === l.id) && !pend.has(l.id)) {
        pend.add(l.id);
        api.recurrentes.create({ id: l.id, concepto: l.concepto || "", monto: l.monto || 0, tipo: l.tipo || "fijo", categoria: l.cat || "Otros", activo: true, dia_mes: l.dia_mes || null, cuenta_id: l.cuenta_id || null })
          .then(res => {
            pend.delete(l.id);
            const row = Array.isArray(res) ? res[0] : res;
            backendSnap.current.recurrentes = [...backendSnap.current.recurrentes, row || { id: l.id }];
          })
          .catch((e) => { pend.delete(l.id); reportar("crear recurrente")(e); });
      }
    }
    const changed = recurrentes.filter(l => {
      const b = snap.find(b => b.id === l.id);
      return b && (l.concepto !== b.concepto || Number(l.monto) !== Number(b.monto) || l.cat !== b.categoria || l.tipo !== b.tipo || (l.dia_mes || null) !== (b.dia_mes || null) || (l.cuenta_id || null) !== (b.cuenta_id || null));
    });
    if (changed.length > 0) {
      clearTimeout(dbTimers.current.recurrentes);
      dbTimers.current.recurrentes = setTimeout(() => {
        changed.forEach(r => {
          const f = { concepto: r.concepto, monto: r.monto, categoria: r.cat, tipo: r.tipo, dia_mes: r.dia_mes || null, cuenta_id: r.cuenta_id || null };
          api.recurrentes.update(r.id, f).then(() => {
            backendSnap.current.recurrentes = backendSnap.current.recurrentes.map(b => b.id === r.id ? { ...b, ...f } : b);
          }).catch(reportar("actualizar entidad global"));
        });
      }, 1500);
    }
  }, [recurrentes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Metas mutations
  const createMeta = useCallback(
    async (data) => {
      const optimisticMeta = { id: crypto.randomUUID(), ...data }; // Temp ID
      setMetas((prev) => [...prev, optimisticMeta]);

      try {
        const result = await api.metas.create(data);
        setMetas((prev) =>
          prev.map((m) => (m.id === optimisticMeta.id ? result : m))
        );
        try { track("meta_creada"); } catch {}
        return result;
      } catch (err) {
        setMetas((prev) => prev.filter((m) => m.id !== optimisticMeta.id));
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const updateMeta = useCallback(
    async (id, fields) => {
      const prev = metas.find((m) => m.id === id);
      const updated = { ...prev, ...fields };
      setMetas((prevMetas) =>
        prevMetas.map((m) => (m.id === id ? updated : m))
      );

      try {
        await api.metas.update(id, fields);
      } catch (err) {
        setMetas((prevMetas) => prevMetas.map((m) => (m.id === id ? prev : m)));
        setError(err.message);
        throw err;
      }
    },
    [metas]
  );

  const deleteMeta = useCallback(
    async (id) => {
      const prev = metas;
      setMetas((prevMetas) => prevMetas.filter((m) => m.id !== id));

      try {
        await api.metas.remove(id);
      } catch (err) {
        setMetas(prev);
        setError(err.message);
        throw err;
      }
    },
    [metas]
  );

  // Presupuestos mutations
  const createPresupuesto = useCallback(
    async (data) => {
      const optimisticPresupuesto = { id: crypto.randomUUID(), ...data };
      setPresupuestos((prev) => [...prev, optimisticPresupuesto]);

      try {
        const result = await api.presupuestos.create(data);
        setPresupuestos((prev) =>
          prev.map((p) => (p.id === optimisticPresupuesto.id ? result : p))
        );
        try { track("presupuesto_creado"); } catch {}
        return result;
      } catch (err) {
        setPresupuestos((prev) =>
          prev.filter((p) => p.id !== optimisticPresupuesto.id)
        );
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const updatePresupuesto = useCallback(
    async (id, fields) => {
      const prev = presupuestos.find((p) => p.id === id);
      const updated = { ...prev, ...fields };
      setPresupuestos((prevPresupuestos) =>
        prevPresupuestos.map((p) => (p.id === id ? updated : p))
      );

      try {
        await api.presupuestos.update(id, fields);
      } catch (err) {
        setPresupuestos((prevPresupuestos) =>
          prevPresupuestos.map((p) => (p.id === id ? prev : p))
        );
        setError(err.message);
        throw err;
      }
    },
    [presupuestos]
  );

  const deletePresupuesto = useCallback(
    async (id) => {
      const prev = presupuestos;
      setPresupuestos((prevPresupuestos) =>
        prevPresupuestos.filter((p) => p.id !== id)
      );

      try {
        await api.presupuestos.remove(id);
      } catch (err) {
        setPresupuestos(prev);
        setError(err.message);
        throw err;
      }
    },
    [presupuestos]
  );

  // Recurrentes mutations
  const createRecurrente = useCallback(
    async (data) => {
      const optimisticRecurrente = { id: crypto.randomUUID(), ...data };
      setRecurrentes((prev) => [...prev, optimisticRecurrente]);

      try {
        const result = await api.recurrentes.create(data);
        setRecurrentes((prev) =>
          prev.map((r) => (r.id === optimisticRecurrente.id ? result : r))
        );
        try { track("recurrente_creado", { tipo: data.tipo || "fijo" }); } catch {}
        return result;
      } catch (err) {
        setRecurrentes((prev) =>
          prev.filter((r) => r.id !== optimisticRecurrente.id)
        );
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const updateRecurrente = useCallback(
    async (id, fields) => {
      const prev = recurrentes.find((r) => r.id === id);
      const updated = { ...prev, ...fields };
      setRecurrentes((prevRecurrentes) =>
        prevRecurrentes.map((r) => (r.id === id ? updated : r))
      );

      try {
        await api.recurrentes.update(id, fields);
      } catch (err) {
        setRecurrentes((prevRecurrentes) =>
          prevRecurrentes.map((r) => (r.id === id ? prev : r))
        );
        setError(err.message);
        throw err;
      }
    },
    [recurrentes]
  );

  const deleteRecurrente = useCallback(
    async (id) => {
      const prev = recurrentes;
      setRecurrentes((prevRecurrentes) =>
        prevRecurrentes.filter((r) => r.id !== id)
      );

      try {
        await api.recurrentes.remove(id);
      } catch (err) {
        setRecurrentes(prev);
        setError(err.message);
        throw err;
      }
    },
    [recurrentes]
  );

  // MSI Compras mutations
  const createMsiCompra = useCallback(
    async (data) => {
      // data ya viene en formato backend (snake_case) desde App.jsx
      const optimisticCompra = {
        id: crypto.randomUUID(),
        concepto: data.concepto || "Compra a meses",
        cat: data.categoria || "Otros",
        totalCompra: Number(data.total_compra) || 0,
        meses: Number(data.num_meses) || 12,
        mesInicio: (Number(data.mes_inicio) || 1) - 1,
        cuentaId: data.cuenta_id || null,
        pagos: {},
      };
      setMsiCompras((prev) => [...prev, optimisticCompra]);

      try {
        const result = await api.msi.compras.create(data);
        // result = { compra: {...}, pagos: [...] }
        const compraRow = result.compra || result;
        const mapped = mapMsiFromBackend(compraRow);
        // Construir pagos map desde los pagos generados
        const pagosArr = result.pagos || [];
        const pagosMap = {};
        pagosArr.forEach(p => {
          const mesIdx = (Number(p.mes) || 1) - 1;
          pagosMap[mesIdx] = { pagado: p.pagado, montoPagado: p.pagado ? p.monto_cuota : null, pagoId: p.id };
        });
        mapped.pagos = pagosMap;

        setMsiCompras((prev) =>
          prev.map((c) => (c.id === optimisticCompra.id ? mapped : c))
        );
        return mapped;
      } catch (err) {
        setMsiCompras((prev) =>
          prev.filter((c) => c.id !== optimisticCompra.id)
        );
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const deleteMsiCompra = useCallback(
    async (id) => {
      const prev = msiCompras;
      setMsiCompras((prevCompras) => prevCompras.filter((c) => c.id !== id));

      try {
        await api.msi.compras.remove(id);
      } catch (err) {
        setMsiCompras(prev);
        setError(err.message);
        throw err;
      }
    },
    [msiCompras]
  );

  // MSI Pagos mutations
  const toggleMsiPago = useCallback(
    async (pagoId, compraId) => {
      // Note: requires fetching compra's pagos to update local state
      // For now, refetch after toggle
      try {
        await api.msi.pagos.toggle(pagoId);
        const pagosArr = await api.msi.pagos.list(compraId);
        // `pagos` es un MAPA indexado por mes 0-11, no el array crudo del
        // backend (que trae mes 1-12). Asignar el array rompía getMsiCuotasMes
        // —que hace pagos[mes]— y dejaba pagoId undefined, con lo que el
        // siguiente toggle ya no llegaba al backend.
        const pagosMap = {};
        (pagosArr || []).forEach((p) => {
          const mesIdx = (Number(p.mes) || 1) - 1;
          pagosMap[mesIdx] = { pagado: p.pagado, montoPagado: p.pagado ? p.monto_cuota : null, pagoId: p.id };
        });
        setMsiCompras((prev) =>
          prev.map((c) => (c.id === compraId ? { ...c, pagos: pagosMap } : c))
        );
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    []
  );

  // Cuentas mutations
  const createCuenta = useCallback(
    async (data) => {
      const optimistic = { id: crypto.randomUUID(), ...data };
      setCuentas((prev) => [...prev, optimistic]);
      try {
        const result = await api.cuentas.create(data);
        setCuentas((prev) => prev.map((c) => (c.id === optimistic.id ? result : c)));
        try { track("cuenta_creada", { tipo: data.tipo || "desconocido" }); } catch {}
        return result;
      } catch (err) {
        setCuentas((prev) => prev.filter((c) => c.id !== optimistic.id));
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const updateCuenta = useCallback(
    async (id, fields) => {
      const prev = cuentas.find((c) => c.id === id);
      const updated = { ...prev, ...fields };
      setCuentas((p) => p.map((c) => (c.id === id ? updated : c)));
      try {
        await api.cuentas.update(id, fields);
      } catch (err) {
        setCuentas((p) => p.map((c) => (c.id === id ? prev : c)));
        setError(err.message);
        throw err;
      }
    },
    [cuentas]
  );

  const deleteCuenta = useCallback(
    async (id) => {
      const prev = cuentas;
      setCuentas((p) => p.filter((c) => c.id !== id));
      try {
        await api.cuentas.remove(id);
      } catch (err) {
        setCuentas(prev);
        setError(err.message);
        throw err;
      }
    },
    [cuentas]
  );

  // Categorias mutations
  const createCategoria = useCallback(
    async (data) => {
      const optimistic = { id: crypto.randomUUID(), activa: true, ...data };
      setCategorias((prev) => [...prev, optimistic]);
      try {
        const result = await api.categorias.create(data);
        setCategorias((prev) => prev.map((c) => (c.id === optimistic.id ? result : c)));
        return result;
      } catch (err) {
        setCategorias((prev) => prev.filter((c) => c.id !== optimistic.id));
        setError(err.message);
        throw err;
      }
    },
    []
  );

  const updateCategoria = useCallback(
    async (id, fields) => {
      const prev = categorias.find((c) => c.id === id);
      const updated = { ...prev, ...fields };
      setCategorias((p) => p.map((c) => (c.id === id ? updated : c)));
      try {
        await api.categorias.update(id, fields);
      } catch (err) {
        setCategorias((p) => p.map((c) => (c.id === id ? prev : c)));
        setError(err.message);
        throw err;
      }
    },
    [categorias]
  );

  const deleteCategoria = useCallback(
    async (id) => {
      const prev = categorias;
      setCategorias((p) => p.filter((c) => c.id !== id));
      try {
        await api.categorias.remove(id);
      } catch (err) {
        setCategorias(prev);
        setError(err.message);
        throw err;
      }
    },
    [categorias]
  );

  const refetch = useCallback(() => {
    fetchGlobalData();
  }, [fetchGlobalData]);

  const refetchTransferencias = useCallback(async () => {
    try {
      const res = await api.transferencias.list();
      setTransferencias(res || []);
    } catch {}
  }, []);

  return {
    // Metas
    metas,
    setMetas,
    createMeta,
    updateMeta,
    deleteMeta,

    // Presupuestos
    presupuestos,
    setPresupuestos,
    createPresupuesto,
    updatePresupuesto,
    deletePresupuesto,

    // Recurrentes
    recurrentes,
    setRecurrentes,
    createRecurrente,
    updateRecurrente,
    deleteRecurrente,

    // MSI Compras
    msiCompras,
    setMsiCompras,
    createMsiCompra,
    deleteMsiCompra,
    toggleMsiPago,

    // Cuentas
    cuentas,
    setCuentas,
    createCuenta,
    updateCuenta,
    deleteCuenta,

    // Categorias
    categorias,
    setCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,

    // Transferencias
    transferencias,
    setTransferencias,
    refetchTransferencias,

    // Preferencias
    diaCobro,
    setDiaCobro,
    nombreUsuario,
    setNombreUsuario,
    onboardingCompleted,
    setOnboardingCompleted,

    // State
    loading,
    error,
    refetch,
  };
}
